import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { App, Button, Modal, ModalFuncProps } from 'antd';
import { ArgsProps } from 'antd/es/notification';

import SelectWithFetchNext from '../../../shared/components/select-with-fetch-next';
import { AppDispatch, RootState } from '../../../store';
import { ChatsCollection, InsuredPhone, LinkToInsuredRequest, SmsChat } from '../models';
import * as actions from '../store/actions';

export type LinkToInsuredModalProps = {
  visible: boolean;
  onClose: () => void;
  smsChatId: string | undefined;
  updateMessageControl: () => void;
};

const LinkToInsuredModal = ({ visible, onClose, smsChatId, updateMessageControl }: LinkToInsuredModalProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { modal, notification } = App.useApp();

  const [selectedInsured, setSelectedInsured] = useState<InsuredPhone>();

  const { insuredsPhonesLoading } = useSelector((state: RootState) => state.smses);

  const { localChats, linkToInsuredResultLoading } = useSelector((state: RootState) => state.smsChat);

  const onLinkInsuredSelect = (value: InsuredPhone) => {
    setSelectedInsured(value);
  };

  const onCancel = () => {
    onClose();
  };

  const onOk = () => {
    if (!smsChatId) {
      return;
    }

    modal.confirm({
      title: 'Are you sure you want to link this insured?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      confirmLoading: linkToInsuredResultLoading,
      okButtonProps: { disabled: linkToInsuredResultLoading },
      onOk() {
        if (!smsChatId || !selectedInsured) {
          return;
        }

        return dispatch(
          actions.linkToInsured({
            smsChatId,
            insuredId: selectedInsured.insuredId,
            insuredName: selectedInsured.insuredName,
          } as LinkToInsuredRequest),
        ).then(() => {
          notification.success({
            message: 'Success',
            top: 55,
            duration: 10,
            description: `Insured was successfully linked`,
          } as ArgsProps);

          let newChatList = localChats?.chatList.slice() ?? [];

          let chatListIndex = newChatList.findIndex((x) => x.smsChatId === smsChatId);
          let chatItem = { ...newChatList[chatListIndex] } as SmsChat;

          chatItem.chatName = selectedInsured?.insuredName;
          chatItem.truckingCompanyId = selectedInsured?.insuredId;

          newChatList[chatListIndex] = chatItem;

          let newChatItems = { ...localChats };
          newChatItems.chatList = newChatList;

          dispatch(actions.setLocalChats(newChatItems as ChatsCollection));
          dispatch(actions.setSelectedChatItem(chatItem));
          updateMessageControl();
          onClose();
        });
      },
    } as ModalFuncProps);
  };

  const onEnterPress = () => {
    if (selectedInsured) {
      onOk();
    }
  };

  return (
    <div
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.shiftKey && selectedInsured) {
          onOk();
        }
      }}
    >
      <Modal
        open={visible}
        title="Link to Insured"
        centered
        onCancel={onCancel}
        onOk={onOk}
        className="link-to-insured-modal"
        footer={[
          <Button key="back" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={linkToInsuredResultLoading || insuredsPhonesLoading}
            onClick={onOk}
            disabled={!selectedInsured}
          >
            Link to Insured
          </Button>,
        ]}
      >
        {visible ? (
          <SelectWithFetchNext
            onSelect={onLinkInsuredSelect}
            dropdownMatchSelectWidth={false}
            keyName={'id'}
            titleName={'display'}
            textName={'display'}
            fetchNext={actions.getInsuredsPhones}
            placeholder="Select or type phone"
            onEnterPress={onEnterPress}
          />
        ) : undefined}
      </Modal>
    </div>
  );
};

export default LinkToInsuredModal;
